import { useState, useEffect, useCallback, useMemo } from "react";
import {
    RefreshCw, Loader2, TrendingUp, AlertTriangle, Ban, Activity,
} from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { AD_PACING_REPORT_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { usePersistedState } from "../../hooks/usePersistedState";
import { cn } from "../../utils/tailwindUtils";
import {
    AdFilterBar, EmptyState, StatTile, money, num, resolveDateRange,
    StatusFilterSelect,
} from "./adReportKit";

/**
 * Daily spend as columns with a lead-count line over the top.
 *
 * Hand-drawn SVG rather than a chart library: this is two series on one axis
 * pair, and pulling in a charting dependency for it would cost more bundle than
 * the whole report. Bars are spend, dots are leads, and the two share an x axis
 * but have independent y scales — labelled, because a dual-axis chart that does
 * not say so is a way of lying with a picture.
 */
const PacingChart = ({ series }) => {
    const { bars, maxSpend, maxLeads } = useMemo(() => {
        const ms = Math.max(...series.map((d) => d.spend), 1);
        const ml = Math.max(...series.map((d) => d.leads), 1);
        return { bars: series, maxSpend: ms, maxLeads: ml };
    }, [series]);

    if (!series.length) return null;

    const W = Math.max(series.length * 22, 320);
    const H = 168;
    const padB = 26;
    const chartH = H - padB - 8;
    const bw = W / series.length;

    const leadPoints = bars
        .map((d, i) => `${i * bw + bw / 2},${8 + chartH - (d.leads / maxLeads) * chartH}`)
        .join(" ");

    return (
        <div className="overflow-x-auto">
            <svg width={W} height={H} role="img"
                aria-label={`Daily spend up to ${Math.round(maxSpend)} rupees and up to ${maxLeads} leads`}
                className="block">
                {[0.25, 0.5, 0.75].map((g) => (
                    <line key={g} x1="0" x2={W} y1={8 + chartH * g} y2={8 + chartH * g}
                        className="stroke-border" strokeWidth="1" strokeDasharray="2 3" />
                ))}
                {bars.map((d, i) => {
                    const h = (d.spend / maxSpend) * chartH;
                    const noLeads = d.spend > 0 && d.leads === 0;
                    return (
                        <rect key={d.iso_date}
                            x={i * bw + 3} y={8 + chartH - h}
                            width={Math.max(bw - 6, 2)} height={Math.max(h, d.spend > 0 ? 1.5 : 0)}
                            rx="1.5"
                            className={noLeads ? "fill-red-400 dark:fill-red-500" : "fill-blue-400 dark:fill-blue-500"}>
                            <title>{`${d.date}\nSpend ${d.spend.toLocaleString("en-IN")}\nLeads ${d.leads}\nCPL ${d.cpl ?? "—"}`}</title>
                        </rect>
                    );
                })}
                <polyline points={leadPoints} fill="none"
                    className="stroke-emerald-500" strokeWidth="1.5" />
                {bars.map((d, i) => (
                    <circle key={d.iso_date} r="2"
                        cx={i * bw + bw / 2}
                        cy={8 + chartH - (d.leads / maxLeads) * chartH}
                        className="fill-emerald-500" />
                ))}
                {bars.map((d, i) =>
                    (series.length <= 32 || i % Math.ceil(series.length / 16) === 0) ? (
                        <text key={d.iso_date} x={i * bw + bw / 2} y={H - 8}
                            textAnchor="middle" className="fill-muted-foreground"
                            style={{ fontSize: 8 }}>
                            {d.date.slice(0, 5)}
                        </text>
                    ) : null,
                )}
            </svg>
        </div>
    );
};

export default function AdSpendPacingReport() {
    const { fetch } = useApiMethods();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [datePreset, setDatePreset] = usePersistedState("ad_pc_preset", "last_month");
    const [customFrom, setCustomFrom] = usePersistedState("ad_pc_from", "");
    const [customTo, setCustomTo] = usePersistedState("ad_pc_to", "");
    const [accountId, setAccountId] = usePersistedState("ad_pc_account", "");
    const [statusFilter, setStatusFilter] = usePersistedState("ad_pc_status", "");
    const [spikePct, setSpikePct] = usePersistedState("ad_pc_spike", "40");

    const load = useCallback(() => {
        if (datePreset === "custom" && (!customFrom || !customTo)) return;
        setLoading(true);
        const { from_date, to_date } = resolveDateRange(datePreset, customFrom, customTo);
        const p = new URLSearchParams();
        if (from_date) p.append("from_date", from_date);
        if (to_date) p.append("to_date", to_date);
        if (accountId) p.append("account_id", accountId);
        if (statusFilter) p.append("status", statusFilter);
        if (spikePct) p.append("cpl_spike_pct", spikePct);

        fetch(`${AD_PACING_REPORT_URL}?${p.toString()}`)
            .then((res) => {
                if (res?.status === "success") setData(res);
                else ToastMsg("error", res?.message || "Failed to load pacing report");
            })
            .catch(() => ToastMsg("error", "Failed to load pacing report"))
            .finally(() => setLoading(false));
    }, [datePreset, customFrom, customTo, accountId, spikePct, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const s = data?.summary;
    const series = data?.series || [];
    const alerts = data?.alerts || [];

    return (
        <ContainerCard>
            <div className="p-2 space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                            <Activity className="h-6 w-6 text-primary" />
                            Spend Pacing
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Day-by-day spend against leads, and the days where cost per lead broke trend.
                        </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={load} disabled={loading}>
                        <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
                        Refresh
                    </Button>
                </div>

                <AdFilterBar
                    {...{ datePreset, setDatePreset, customFrom, setCustomFrom, customTo, setCustomTo,
                        accountId, setAccountId, accounts: data?.filters?.accounts || [] }}
                >
                    <StatusFilterSelect value={statusFilter} onChange={setStatusFilter} />
                    <select value={spikePct} onChange={(e) => setSpikePct(e.target.value)}
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground">
                        <option value="25">Flag CPL +25%</option>
                        <option value="40">Flag CPL +40%</option>
                        <option value="75">Flag CPL +75%</option>
                        <option value="150">Flag CPL +150%</option>
                    </select>
                </AdFilterBar>

                {s && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        <StatTile tone="spend" label="Spend" value={money(s.spend)}
                            sub={`${money(s.avg_daily_spend)}/day over ${s.days}d`} />
                        <StatTile tone="leads" label="Leads" value={num(s.leads)} />
                        <StatTile tone="cost" label="Cost / Lead" value={money(s.cpl)} />
                        <StatTile label="Peak day" value={money(s.peak_day_spend)} />
                        <StatTile tone={s.zero_lead_days ? "bad" : "default"}
                            label="Days with no leads" value={num(s.zero_lead_days)}
                            sub="spend but zero leads" />
                        <StatTile tone={s.alert_count ? "bad" : "default"}
                            label="Alerts" value={num(s.alert_count)}
                            sub={`CPL over +${s.cpl_spike_pct}%`} />
                    </div>
                )}

                {loading && (
                    <div className="flex items-center justify-center py-16 text-muted-foreground">
                        <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                )}

                {!loading && series.length === 0 && (
                    <EmptyState title="No spend recorded in this period"
                        hint="Try a wider date range, or sync the account from Settings → Meta Ads." />
                )}

                {!loading && series.length > 0 && (
                    <>
                        <div className="rounded-xl border border-border bg-card p-4">
                            <div className="flex flex-wrap items-center gap-4 mb-3 text-[11px]">
                                <span className="flex items-center gap-1.5">
                                    <span className="h-2.5 w-2.5 rounded-sm bg-blue-400 dark:bg-blue-500" />
                                    <span className="text-muted-foreground">Daily spend (left scale)</span>
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="h-0.5 w-4 bg-emerald-500" />
                                    <span className="text-muted-foreground">Leads (right scale)</span>
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="h-2.5 w-2.5 rounded-sm bg-red-400 dark:bg-red-500" />
                                    <span className="text-muted-foreground">Spend, no leads</span>
                                </span>
                            </div>
                            <PacingChart series={series} />
                        </div>

                        {alerts.length > 0 && (
                            <div className="rounded-xl border border-border bg-card overflow-hidden">
                                <div className="px-4 py-2.5 border-b border-border bg-muted/40">
                                    <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                                        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                                        {alerts.length} day{alerts.length === 1 ? "" : "s"} worth a look
                                    </h2>
                                </div>
                                <div className="divide-y divide-border max-h-72 overflow-y-auto">
                                    {alerts.map((a, i) => (
                                        <div key={`${a.date}-${i}`} className="flex items-start gap-3 px-4 py-2.5">
                                            {a.type === "no_leads"
                                                ? <Ban className="h-3.5 w-3.5 mt-0.5 text-red-600 dark:text-red-400 shrink-0" />
                                                : <TrendingUp className="h-3.5 w-3.5 mt-0.5 text-amber-600 dark:text-amber-400 shrink-0" />}
                                            <div className="min-w-0">
                                                <p className="text-xs font-medium text-foreground">{a.date}</p>
                                                <p className="text-[11px] text-muted-foreground">{a.message}</p>
                                            </div>
                                            {a.jump_pct != null && (
                                                <span className="ml-auto text-xs font-semibold text-amber-700 dark:text-amber-400 tabular-nums shrink-0">
                                                    +{a.jump_pct}%
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="overflow-x-auto rounded-lg border border-border">
                            <table className="w-full text-xs">
                                <thead className="bg-muted/50">
                                    <tr className="text-muted-foreground">
                                        <th className="px-3 py-2.5 text-left font-medium">Date</th>
                                        <th className="px-3 py-2.5 text-right font-medium">Spend</th>
                                        <th className="px-3 py-2.5 text-right font-medium">Cumulative</th>
                                        <th className="px-3 py-2.5 text-right font-medium">Impressions</th>
                                        <th className="px-3 py-2.5 text-right font-medium">Clicks</th>
                                        <th className="px-3 py-2.5 text-right font-medium">Leads</th>
                                        <th className="px-3 py-2.5 text-right font-medium">CPL</th>
                                        <th className="px-3 py-2.5 text-right font-medium">CRM</th>
                                        <th className="px-3 py-2.5 text-right font-medium">Demos</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {[...series].reverse().map((d) => (
                                        <tr key={d.iso_date}
                                            className={cn("hover:bg-accent/30",
                                                d.spend > 0 && d.leads === 0 && "bg-red-50/50 dark:bg-red-900/10")}>
                                            <td className="px-3 py-2 text-foreground whitespace-nowrap">{d.date}</td>
                                            <td className="px-3 py-2 text-right font-medium text-foreground tabular-nums">{money(d.spend)}</td>
                                            <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{money(d.cumulative)}</td>
                                            <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{num(d.impressions)}</td>
                                            <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{num(d.clicks)}</td>
                                            <td className="px-3 py-2 text-right text-foreground font-medium tabular-nums">{num(d.leads)}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">
                                                <span className={d.cpl == null ? "text-muted-foreground" : "text-amber-700 dark:text-amber-400 font-medium"}>
                                                    {money(d.cpl)}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{num(d.crm_leads)}</td>
                                            <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{num(d.demos)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <p className="text-[11px] text-muted-foreground">
                            A day is flagged when its CPL exceeds the median of the previous 7 days by
                            more than the chosen threshold. The median is used rather than the mean so a
                            single runaway day cannot raise the bar enough to hide the days after it.
                        </p>
                    </>
                )}
            </div>
        </ContainerCard>
    );
}
